import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  Form,
  Icon,
  InlineGrid,
  InlineStack,
  ProgressBar,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { TypedVehicle, useGetVehicles } from 'queries/vehicle.query';
import { MenuIcon, ChartLineIcon, CheckboxIcon } from '@shopify/polaris-icons';
import 'media/css/vehicle_tracking.scss';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import helpers from 'helpers/index';
import { useSettings } from 'context/SettingsContext';

const defaultCenter = { lat: 21.0278, lng: 105.8342 };
type VehicleMarkerPoint = {
  id: string;
  vehicleId: string | null;
  vehicleImei: string | null;
  isOnline: boolean;
  isMoving: boolean;
  isCharging: boolean;
  isAccOn: boolean;
  name: string;
  licensePlate: string;
  batteryPercent: string;
  speed: string;
  gear: string;
  odo: string;
  acStatus: string;
  tpmsPressure: string;
  updatedAt: string;
  coordinates: [number, number];
};

type MapboxGlobals = {
  accessToken: string;
  Map: new (...args: any[]) => any;
  Marker: new (...args: any[]) => any;
  Popup: new (...args: any[]) => any;
  LngLatBounds: new (...args: any[]) => any;
};

declare global {
  interface Window {
    mapboxgl?: MapboxGlobals;
  }
}

const MAPBOX_SCRIPT_ID = 'mapbox-gl-js';
const MAPBOX_STYLE_ID = 'mapbox-gl-css';
const MAPBOX_SCRIPT_SRC = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js';
const MAPBOX_CSS_SRC = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css';

const parseVehicleLocation = (location?: string | null): [number, number] | null => {
  if (!location?.trim()) return null;

  const [lat, lng] = location.split(',').map((item) => Number(item?.trim()));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (lat === 0 && lng === 0) return null;

  return [lng, lat];
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const createMarkerPopupHtml = (point: VehicleMarkerPoint): string => {
  return `
    <div class="vehicle-map-popup">
      <p><strong>${escapeHtml(point.name)}</strong></p>
      <p><strong>${escapeHtml(point.licensePlate)}</strong></p>
      <p>${point.isCharging ? '⚡ Đang sạc' : '⚡ Không sạc'} • ${point.isAccOn ? 'ACC bật' : 'ACC tắt'} • ${
        point.isOnline ? 'Online' : 'Offline'
      }</p>
      <p>ODO: ${escapeHtml(point.odo)}</p>
      <p>Điều hòa: ${escapeHtml(point.acStatus)}</p>
      <p>Áp suất lốp: <strong>${escapeHtml(point.tpmsPressure)}</strong></p>
      <p>Cập nhật: ${escapeHtml(point.updatedAt)}</p>
      <p>Nhiên liệu: ${escapeHtml(point.batteryPercent)} • Tốc độ: ${escapeHtml(point.speed)} • Số: ${escapeHtml(point.gear)}</p>
    </div>
  `;
};

const loadMapboxAssets = () => {
  if (!document.getElementById(MAPBOX_STYLE_ID)) {
    const link = document.createElement('link');
    link.id = MAPBOX_STYLE_ID;
    link.rel = 'stylesheet';
    link.href = MAPBOX_CSS_SRC;
    document.head.appendChild(link);
  }

  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);

  return new Promise<MapboxGlobals>((resolve, reject) => {
    const existingScript = document.getElementById(MAPBOX_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.mapboxgl as MapboxGlobals));
      existingScript.addEventListener('error', () => reject(new Error('Không tải được Mapbox script.')));
      return;
    }

    const script = document.createElement('script');
    script.id = MAPBOX_SCRIPT_ID;
    script.src = MAPBOX_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl as MapboxGlobals);
    script.onerror = () => reject(new Error('Không tải được Mapbox script.'));
    document.body.appendChild(script);
  });
};

export default function VehicleTracking() {
  // const { isPending: loadingVehicleLogs, mutateAsync: getVehicleLogs } = useMutateGetVehiclelogs();

  const { settings } = useSettings();
  const { _mapbox_public_key = '' } = settings || {};

  const history = useNavigate();
  let { search } = useLocation();
  let StringQuery: any = helpers.ExtractUrl(search);

  const initialQuery = {
    query: '',
    page: 1,
    limit: 25,
    sort: 'createdAt:desc',
  };
  const [mainQuery, setMainQuery] = useState({
    ...initialQuery,
    ...StringQuery,
  });

  const { data: vehicleResponse, isLoading: loadingVehicles, refetch: getEntities } = useGetVehicles(mainQuery);
  /**
   * Double request!
   */
  useEffect(() => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) history('/vehicle_tracking' + buildURLSearch);
    getEntities();
  }, [mainQuery]);

  const onQueryToServerDebound = useCallback((keyword) => {
    setMainQuery((old) => ({ ...old, ...{ query: keyword } }));
  }, []);
  const onChangeMainQueryCallback = useMemo(() => {
    return helpers.debounce((_value: string) => {
      onQueryToServerDebound?.call(this, _value);
    }, 400);
  }, []);

  const [currentSearchValue, setCurrentSearchValue] = useState(mainQuery.query || '');
  const [mapboxReady, setMapboxReady] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const hasAppliedInitialViewportRef = useRef(false);
  const lastFocusedVehicleIdRef = useRef<string | null>(null);
  const suppressNextFocusFlyRef = useRef(false);
  const [mapReadyTick, setMapReadyTick] = useState(0);
  const [cachedInitialMapPoints, setCachedInitialMapPoints] = useState<VehicleMarkerPoint[]>([]);

  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const sortedVehicles = useMemo(() => {
    const vehicles = vehicleResponse?.body || [];
    return [...vehicles].sort((vehicleA, vehicleB) => {
      const vehicleAOnline = vehicleA.vehicle_meta?.vehicle_online === 1 ? 1 : 0;
      const vehicleBOnline = vehicleB.vehicle_meta?.vehicle_online === 1 ? 1 : 0;
      return vehicleBOnline - vehicleAOnline;
    });
  }, [vehicleResponse]);
  const selectedVehicle = useMemo(() => {
    if (!selectedVehicleId) return undefined;
    return sortedVehicles.find((vehicle) => vehicle.id === selectedVehicleId);
  }, [selectedVehicleId, sortedVehicles]);
  const mapVehiclePoints = useMemo<VehicleMarkerPoint[]>(() => {
    return (
      sortedVehicles
        ?.map((vehicle) => {
          const coordinates = parseVehicleLocation(vehicle.vehicle_meta?.vehicle_location);
          if (!coordinates) return null;
          const vehicleMeta = vehicle.vehicle_meta;
          const vehicleTpms = vehicleMeta?.vehicle_tpms ? JSON.parse(vehicleMeta.vehicle_tpms) : null;
          const vehicleAc = vehicleMeta?.vehicle_ac ? JSON.parse(vehicleMeta.vehicle_ac) : null;
          return {
            id: vehicle.id || vehicle.vehicle_imei || coordinates.join('-'),
            vehicleId: vehicle.id || null,
            vehicleImei: vehicle.vehicle_imei || null,
            isOnline: vehicleMeta?.vehicle_online === 1,
            isMoving: Number(vehicleMeta?.vehicle_speed || 0) > 0,
            isCharging: vehicleMeta?.vehicle_charging === 1,
            isAccOn: vehicleMeta?.vehicle_acc === 1,
            name: vehicle.name || 'Không tên',
            licensePlate: vehicle.license_plate || vehicle.vehicle_imei || 'Không có biển số',
            batteryPercent: vehicleMeta?.vehicle_fuel ? `${vehicleMeta.vehicle_fuel}%` : '0%',
            speed: `${String(vehicleMeta?.vehicle_speed || '0')} km/h`,
            gear: String(vehicleMeta?.vehicle_gear || 'n').toUpperCase(),
            odo: vehicleMeta?.vehicle_odo ? `${String(vehicleMeta.vehicle_odo)} km` : 'Không có dữ liệu',
            acStatus:
              vehicleAc?.compressor === 1 ? `${vehicleAc.tempMain}°C / Ngoài trời ${vehicleAc.tempOut}°C / Gió ${vehicleAc.windLevel}` : 'Tắt',
            tpmsPressure: vehicleTpms
              ? `${vehicleTpms.lfPressure}/ ${vehicleTpms.rfPressure}/ ${vehicleTpms.lbPressure}/ ${vehicleTpms.rbPressure}`
              : 'Không có dữ liệu',
            updatedAt: vehicleMeta?.updatedAt ? new Date(Number(vehicleMeta.updatedAt)).toLocaleString() : 'Không có dữ liệu',
            coordinates,
          };
        })
        .filter((item): item is VehicleMarkerPoint => Boolean(item)) || []
    );
  }, [sortedVehicles]);
  const selectedVehiclePoint = useMemo(() => {
    if (!selectedVehicle) return null;
    return mapVehiclePoints.find((item) => item.id === selectedVehicle.id || item.licensePlate === selectedVehicle.license_plate) || null;
  }, [mapVehiclePoints, selectedVehicle]);
  const mapPointsForViewport = useMemo(() => {
    const isSearching = Boolean(mainQuery.query?.trim());
    if (!isSearching) return mapVehiclePoints;
    return cachedInitialMapPoints.length ? cachedInitialMapPoints : mapVehiclePoints;
  }, [cachedInitialMapPoints, mainQuery.query, mapVehiclePoints]);
  const onSelectVehicleFromMarker = useCallback(
    (point: VehicleMarkerPoint) => {
      const matchedVehicle = vehicleResponse?.body?.find(
        (vehicle) => vehicle.id === point.vehicleId || vehicle.vehicle_imei === point.vehicleImei || vehicle.license_plate === point.licensePlate
      );
      if (!matchedVehicle?.id) return;
      suppressNextFocusFlyRef.current = true;
      setSelectedVehicleId(matchedVehicle.id);
    },
    [vehicleResponse]
  );

  useEffect(() => {
    let isMounted = true;
    loadMapboxAssets()
      .then(() => {
        if (!isMounted) return;
        setMapboxReady(true);
      })
      .catch((error) => {
        if (!isMounted) return;
        // setMapboxError(error?.message || 'Không thể tải Mapbox.');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (mainQuery.query?.trim()) return;
    if (!mapVehiclePoints.length) return;
    setCachedInitialMapPoints((previousPoints) => {
      if (mapVehiclePoints.length <= previousPoints.length) return previousPoints;
      return mapVehiclePoints;
    });
  }, [mainQuery.query, mapVehiclePoints]);

  useEffect(() => {
    if (!mapboxReady || mapRef.current || !mapContainerRef.current || !window.mapboxgl) return;
    if (!_mapbox_public_key) {
      // setMapboxError('Thiếu Mapbox Access Token (_maps_key).');
      return;
    }

    const mapboxgl = window.mapboxgl;
    mapboxgl.accessToken = _mapbox_public_key;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [defaultCenter.lng, defaultCenter.lat],
      zoom: 5.5,
      pitch: 0,
      bearing: 0,
      antialias: true,
    });

    mapRef.current = map;
    setMapReadyTick((prev) => prev + 1);

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [mapboxReady, _mapbox_public_key]);

  const updateMapViewport = useCallback(
    ({ shouldAdjustViewport }: { shouldAdjustViewport: boolean }) => {
      const map = mapRef.current;
      const mapboxgl = window.mapboxgl;
      if (!map || !mapboxgl) return;

      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      const pointsToRender = mapPointsForViewport;
      pointsToRender.forEach((point) => {
        const isFocusedMarker =
          selectedVehiclePoint?.id === point.id ||
          (selectedVehiclePoint?.vehicleId && selectedVehiclePoint.vehicleId === point.vehicleId) ||
          (selectedVehiclePoint?.vehicleImei && selectedVehiclePoint.vehicleImei === point.vehicleImei);
        const markerElement = document.createElement('div');
        markerElement.className = `vehicle-map-marker ${isFocusedMarker ? 'vehicle-map-marker--focused' : ''} ${
          point.isOnline ? '' : 'vehicle-map-marker--offline'
        }`.trim();
        markerElement.setAttribute('draggable', 'false');
        markerElement.innerHTML = `
        ${isFocusedMarker ? '<span class="vehicle-map-marker__focus-ring" aria-hidden="true"></span>' : ''}
        ${point.isMoving ? '<span class="vehicle-map-marker__movement-ring" aria-hidden="true"></span>' : ''}
        <div class="vehicle-map-marker__pin">
          <span class="vehicle-map-marker__car" aria-hidden="true">🚗</span>
          <span class="vehicle-map-marker__tip" aria-hidden="true"></span>
        </div>
        <span class="vehicle-map-marker__plate">${point.licensePlate} - ${point.batteryPercent}</span>
      `;
        if (point.isMoving) {
          markerElement.classList.add('vehicle-map-marker--moving');
        }
        markerElement.addEventListener('dragstart', (event) => {
          event.preventDefault();
        });

        const marker = new mapboxgl.Marker({ element: markerElement, anchor: 'bottom' })
          .setLngLat(point.coordinates)
          .setPopup(new mapboxgl.Popup({ offset: 24 }).setHTML(createMarkerPopupHtml(point)))
          .addTo(map);

        markerElement.addEventListener('click', () => onSelectVehicleFromMarker(point));
        markersRef.current.push(marker);
      });

      map.resize();

      if (!shouldAdjustViewport) return;

      if (!pointsToRender.length) {
        if (!loadingVehicles) {
          map.flyTo({ center: [defaultCenter.lng, defaultCenter.lat], zoom: 5.5, pitch: 0, bearing: 0, essential: true });
        }
        return;
      }

      if (selectedVehiclePoint) {
        map.flyTo({ center: selectedVehiclePoint.coordinates, zoom: 15.5, pitch: 0, bearing: 0, essential: true });
        return;
      }

      if (pointsToRender.length === 1) {
        map.flyTo({ center: pointsToRender[0].coordinates, zoom: 15.5, pitch: 0, bearing: 0, essential: true });
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      pointsToRender.forEach((point) => bounds.extend(point.coordinates));
      map.fitBounds(bounds, { padding: 90, duration: 1000, maxZoom: 14 });
    },
    [loadingVehicles, mapPointsForViewport, onSelectVehicleFromMarker, selectedVehiclePoint]
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const shouldAdjustViewport = !hasAppliedInitialViewportRef.current && !loadingVehicles;

    if (map.isStyleLoaded()) {
      updateMapViewport({ shouldAdjustViewport });
      if (shouldAdjustViewport) hasAppliedInitialViewportRef.current = true;
      return;
    }

    const handleMapLoad = () => {
      updateMapViewport({ shouldAdjustViewport });
      if (shouldAdjustViewport) hasAppliedInitialViewportRef.current = true;
    };

    map.once('load', handleMapLoad);
    return () => {
      map.off('load', handleMapLoad);
    };
  }, [loadingVehicles, mapReadyTick, updateMapViewport]);

  useEffect(() => {
    if (!selectedVehiclePoint) return;
    if (lastFocusedVehicleIdRef.current === selectedVehiclePoint.id) return;

    if (suppressNextFocusFlyRef.current) {
      suppressNextFocusFlyRef.current = false;
      lastFocusedVehicleIdRef.current = selectedVehiclePoint.id;
      return;
    }

    const map = mapRef.current;
    if (!map) return;

    map.flyTo({ center: selectedVehiclePoint.coordinates, zoom: 15.5, pitch: 0, bearing: 0, essential: true });
    lastFocusedVehicleIdRef.current = selectedVehiclePoint.id;
  }, [selectedVehiclePoint]);

  useEffect(() => {
    if (selectedVehicleId) return;
    lastFocusedVehicleIdRef.current = null;
  }, [selectedVehicleId]);

  // const vehicleLogsCallback = useCallback(
  //   (vehicle_imei) => {
  //     getVehicleLogs(vehicle_imei).then((response) => {
  //       const logs = response.body;
  //     });
  //   },
  //   [getVehicleLogs]
  // );

  const CurrentVehicleDataToShow = ({ selectedVehicle }: { selectedVehicle: TypedVehicle }) => {
    if (!selectedVehicle) return null;
    const vehicle_ac = selectedVehicle.vehicle_meta?.vehicle_ac ? JSON.parse(selectedVehicle.vehicle_meta.vehicle_ac) : null;
    const vehicle_tpms = selectedVehicle.vehicle_meta?.vehicle_tpms ? JSON.parse(selectedVehicle.vehicle_meta.vehicle_tpms) : null;
    return (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd" tone="subdued">
          {selectedVehicle.name || 'Không tên'}
        </Text>
        <Text as="h2" variant="headingLg">
          {selectedVehicle.license_plate}
        </Text>
        {/** trang trí cho giống thông tin xe điện */}

        <InlineGrid gap="200" columns={2}>
          <Card padding={'200'}>
            <InlineStack gap="100" align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd" tone="subdued">
                PIN
              </Text>
              <Text as="h3" variant="headingMd" tone="subdued">
                <Icon source={ChartLineIcon} tone="success" />
              </Text>
            </InlineStack>

            <Text as="h1" variant="heading2xl">
              {selectedVehicle.vehicle_meta?.vehicle_fuel ? String(selectedVehicle.vehicle_meta.vehicle_fuel) + '%' : '--'}
            </Text>

            <ProgressBar progress={75} animated={true} tone="success" size="small" />
            <Text as="p" variant="bodyMd">
              {selectedVehicle.vehicle_meta?.vehicle_fuel_range || '--'}km nữa sẽ hết pin
            </Text>
          </Card>

          <Card padding={'200'}>
            <InlineStack gap="100" align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd" tone="subdued">
                Điều hòa
              </Text>
              <Text as="h3" variant="headingMd" tone="subdued">
                {vehicle_ac?.power === 1 ? <Icon source={CheckboxIcon} /> : ''}
              </Text>
            </InlineStack>

            <Text as="h1" variant="heading2xl">
              {vehicle_ac ? vehicle_ac.tempMain + ' °C' : '--'}
            </Text>

            <Text as="p" variant="bodyMd">
              Ngoài trời: {vehicle_ac?.tempOut || '--'} °C
            </Text>
          </Card>
        </InlineGrid>

        <InlineGrid gap="200" columns={2}>
          <Card padding={'200'}>
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd" tone="subdued">
                Áp suất lốp
              </Text>
              <Divider />
              <InlineStack align="space-between" gap="100">
                <div>
                  <Text as="p" variant="bodySm">
                    Trước trái
                  </Text>
                  <Text as="h2" variant="headingLg">
                    {vehicle_tpms?.lfPressure || '--'}
                  </Text>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text as="p" variant="bodySm">
                    Trước phải
                  </Text>
                  <Text as="h2" variant="headingLg">
                    {vehicle_tpms?.rfPressure || '--'}
                  </Text>
                </div>
                <div>
                  <Text as="p" variant="bodySm">
                    Sau trái
                  </Text>
                  <Text as="h2" variant="headingLg">
                    {vehicle_tpms?.lbPressure || '--'}
                  </Text>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text as="p" variant="bodySm">
                    Sau phải
                  </Text>
                  <Text as="h2" variant="headingLg">
                    {vehicle_tpms?.rbPressure || '--'}
                  </Text>
                </div>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card padding={'200'}>
            <BlockStack gap="200">
              <InlineStack gap="100" align="space-between" blockAlign="center">
                <Text as="h3" variant="headingMd" tone="subdued">
                  Tốc độ
                </Text>
                <Text as="h3" variant="headingMd" tone="subdued">
                  P
                </Text>
              </InlineStack>
              <Divider />
              <Text as="h1" variant="heading2xl">
                {selectedVehicle.vehicle_meta?.vehicle_speed || '--'} km/h
              </Text>
            </BlockStack>
          </Card>
        </InlineGrid>

        <Divider />
        <Divider />
        <Text as="p" variant="bodySm" tone="subdued">
          Cập nhật lần cuối:{' '}
          {selectedVehicle.vehicle_meta?.updatedAt ? new Date(Number(selectedVehicle.vehicle_meta.updatedAt)).toLocaleString() : '--'}
        </Text>
      </BlockStack>
    );
  };

  const [isLeftContainerExpanded, setIsLeftContainerExpanded] = useState(false);

  useEffect(() => {
    if (!isLeftContainerExpanded) {
      setCurrentSearchValue('');
      setSelectedVehicleId(null);
    }
  }, [isLeftContainerExpanded]);

  return (
    <>
      <Helmet>
        <title>Vehicle Log</title>
      </Helmet>
      <div id="vehicle-log-container">
        <div ref={mapContainerRef} className="vehicle-log-map" />
        {/* {mapboxError && (
          <div className="vehicle-log-map-error">
            <Text as="p" variant="bodyMd" tone="critical">
              {mapboxError}
            </Text>
          </div>
        )} */}

        <div className={`vehicle-log-left-container`.trim()}>
          <div className="box_element">
            <Box padding={'200'}>
              <Form onSubmit={() => {}} noValidate={true}>
                <TextField
                  label=""
                  onFocus={() => {
                    setIsLeftContainerExpanded(true);
                  }}
                  connectedLeft={
                    <Button
                      variant="monochromePlain"
                      icon={MenuIcon}
                      onClick={() => {
                        setIsLeftContainerExpanded((prev) => !prev);
                      }}
                    ></Button>
                  }
                  placeholder="Nhập tên hoặc biển số"
                  autoComplete="off"
                  value={currentSearchValue}
                  onChange={(value) => {
                    onChangeMainQueryCallback(value);
                    setCurrentSearchValue(value);
                  }}
                />
              </Form>
            </Box>

            <div className={`vehicle_tracking_list_content ${isLeftContainerExpanded ? 'result_expanded' : ''}`}>
              {loadingVehicles ? (
                <Box padding="400">
                  <InlineStack align="center">
                    <Spinner size="small" />
                  </InlineStack>
                </Box>
              ) : (
                <InlineStack gap="200" blockAlign="start" wrap={false}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    {sortedVehicles.map((vehicle) => {
                      const isActive = selectedVehicle?.id === vehicle.id;
                      const vehicle_meta = vehicle.vehicle_meta;
                      const vehicle_tpms = vehicle_meta?.vehicle_tpms ? JSON.parse(vehicle_meta.vehicle_tpms) : null;
                      const vehicle_ac = vehicle_meta?.vehicle_ac ? JSON.parse(vehicle_meta.vehicle_ac) : null;

                      return (
                        <div
                          key={vehicle.id || vehicle.vehicle_imei}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setSelectedVehicleId(vehicle.id);
                            setIsLeftContainerExpanded(false);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') setSelectedVehicleId(vehicle.id);
                          }}
                        >
                          <Box
                            padding="300"
                            background={isActive ? 'bg-surface-emphasis' : vehicle_meta?.vehicle_online === 1 ? 'bg-surface' : 'bg-fill-disabled'}
                          >
                            <div className={`vehicle-log-item ${isActive ? 'active' : ''} `}>
                              <BlockStack gap="100">
                                <InlineStack gap="100" align="start" blockAlign="center">
                                  <Badge
                                    tone={vehicle_meta?.vehicle_online === 1 && vehicle_meta?.vehicle_acc === 1 ? 'success-strong' : 'read-only'}
                                  >
                                    ACC
                                  </Badge>
                                  <Badge
                                    tone={vehicle_meta?.vehicle_online === 1 && vehicle_meta?.vehicle_charging === 1 ? 'success-strong' : 'read-only'}
                                  >
                                    Charging
                                  </Badge>

                                  <Badge tone={vehicle_meta?.vehicle_online === 1 ? 'success-strong' : 'read-only'}>
                                    {String(vehicle_meta?.vehicle_fuel || '0') + '%'}
                                  </Badge>
                                  <Badge tone={vehicle_meta?.vehicle_online === 1 ? 'success-strong' : 'read-only'}>
                                    {String(vehicle_meta?.vehicle_speed || '0') + ' km/h'}
                                  </Badge>
                                  <Badge tone={vehicle_meta?.vehicle_online === 1 ? 'success-strong' : 'read-only'}>
                                    {String(vehicle_meta?.vehicle_gear || 'n').toUpperCase()}
                                  </Badge>

                                  <Badge tone={vehicle_meta?.vehicle_online === 1 ? 'success-strong' : 'read-only'}>
                                    {vehicle_meta?.vehicle_online === 1 ? 'Online' : 'Offline'}
                                  </Badge>
                                </InlineStack>

                                <Text as="h2" variant="headingLg">
                                  {vehicle.license_plate || vehicle.vehicle_imei || 'Không có biển số'}
                                </Text>

                                <Divider />

                                <Text as="p" variant="bodySm" tone="subdued">
                                  ODO: {vehicle_meta?.vehicle_odo ? String(vehicle_meta.vehicle_odo) + ' km' : 'Không có dữ liệu'}
                                </Text>

                                <Text as="p" variant="bodySm" tone="subdued">
                                  Điều hòa:{' '}
                                  {vehicle_ac?.compressor === 1
                                    ? `${vehicle_ac.tempMain}°C / Ngoài trời ${vehicle_ac.tempOut}°C / Gió ${vehicle_ac.windLevel}`
                                    : 'Tắt'}
                                </Text>

                                <Text as="p" variant="bodySm" tone="subdued">
                                  Áp suất lốp:{' '}
                                  <strong>
                                    {vehicle_tpms
                                      ? `${vehicle_tpms.lfPressure}/ ${vehicle_tpms.rfPressure}/ ${vehicle_tpms.lbPressure}/ ${vehicle_tpms.rbPressure}`
                                      : 'Không có dữ liệu'}
                                  </strong>
                                </Text>

                                <Text as="p" variant="bodySm" tone="subdued">
                                  Cập nhật: {vehicle_meta?.updatedAt ? new Date(Number(vehicle_meta.updatedAt)).toLocaleString() : 'Không có dữ liệu'}
                                </Text>
                              </BlockStack>
                            </div>
                          </Box>
                        </div>
                      );
                    })}
                  </div>
                </InlineStack>
              )}
            </div>
            {/** vehicle_tracking_list_content  */}
          </div>
          {/** vehicle_tracking_left_column_wrap  */}
          {/* {selectedVehicle && (
            <div className="box_element">
              <Box padding="400">
                <Button variant="monochromePlain" icon={ArrowLeftIcon} onClick={() => setSelectedVehicleId(null)}>
                  Quay lại
                </Button>
              </Box>
              <Box padding="400">
                <CurrentVehicleDataToShow selectedVehicle={selectedVehicle} />
              </Box>
            </div>
          )} */}
        </div>
        {/** vehicle-log-left-container  */}
      </div>
    </>
  );
}

